import React, { useEffect } from "react";
import { Card, Grid, Stack, CircularProgress } from "@mui/material";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";

import {
  IoSparkles,
  IoRestaurant,
  IoSunny,
  IoMoon,
  IoLocationSharp,
  IoPerson,
  IoPeople,
  IoHome,
  IoBed,
  IoPaw,
  IoThermometer,
  IoCheckmarkCircle,
  IoStatsChart,
} from "react-icons/io5";
import { usePreferenceInsights } from "hooks/usePreferenceInsights";
import { getInsightColor } from "utils/preferenceClassifier";

import colors from "assets/theme/base/colors";
import rgba from "assets/theme/functions/rgba";

import useCommon from "hooks/useCommon";
import { getDashboardPreferenceInsights } from "store/slice/dashboardSlice";

const ICON_MAP = {
  IoSparkles,
  IoRestaurant,
  IoSunny,
  IoMoon,
  IoLocationSharp,
  IoPerson,
  IoPeople,
  IoHome,
  IoBed,
  IoPaw,
  IoThermometer,
  IoCheckmarkCircle,
  IoStatsChart,
};

const { dark, light, primary } = colors;

const getIcon = (name, options = {}) => {
  const { size = 14, color, context } = options;

  const Icon = ICON_MAP[name] || IoSparkles;

  const resolvedColor = color || (context ? getInsightColor(context) : primary.main);

  return <Icon color={resolvedColor} size={`${size}px`} />;
};

const renderGlassCard = ({ children, px = 1.25, py = 1, p }) => (
  <VuiBox
    px={p ?? px}
    py={p ?? py}
    borderRadius="12px"
    sx={{
      height: "100%",
      // background: "rgba(255,255,255,0.03)",
      border: `1px solid ${rgba(dark.main, 0.25)}`,
      backdropFilter: "blur(12px)",
    }}
  >
    {children}
  </VuiBox>
);

function GuestLifestyleInsights() {
  const { DISPATCH, STORE } = useCommon();

  const selectedHotelId = STORE((state) => state.ui.hotelId);

  const {
    preferenceInsights: insights,
    preferenceInsightsLoading,
    preferenceInsightsError,
  } = STORE((state) => state.dashboard);

  useEffect(() => {
    if (selectedHotelId) {
      DISPATCH(
        getDashboardPreferenceInsights({
          hotelId: selectedHotelId,
          questionnaireVersion: 1,
        })
      );
    }
  }, [DISPATCH, selectedHotelId]);

  {
    /* ================= LOADING STATE ================= */
  }

  if (preferenceInsightsLoading) {
    return (
      <Card>
        <VuiBox
          minHeight={260}
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          px={3}
        >
          <CircularProgress size={26} color="primary" />
        </VuiBox>
      </Card>
    );
  }

  {
    /* ================= EMPTY STATE ================= */
  }

  if (preferenceInsightsError || !insights || !insights.insightCards?.length) {
    return (
      <Card>
        <VuiBox
          minHeight={280}
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          px={3}
        >
          <VuiBox
            mb={2}
            sx={{
              height: 56,
              width: 56,
              borderRadius: "16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(62,201,255,0.08)",
              border: "1px solid rgba(62,201,255,0.2)",
              backdropFilter: "blur(8px)",
            }}
          >
            <IoSparkles size={24} color={primary.main} />
          </VuiBox>

          <VuiTypography variant="lg" fontWeight="bold" sx={{ mb: 0.5 }}>
            {preferenceInsightsError ? "Unable to Load Insights" : "No Insights Yet"}
          </VuiTypography>

          <VuiTypography
            variant="caption"
            sx={{
              maxWidth: 320,
              opacity: 0.75,
              lineHeight: 1.5,
            }}
          >
            {preferenceInsightsError
              ? preferenceInsightsError || "Something went wrong while loading preference insights."
              : "We’ll start generating insights as soon as guests begin sharing their preferences. This helps you understand behavior and personalize their stay."}
          </VuiTypography>

          {!preferenceInsightsError && (
            <>
              <VuiBox
                mt={2}
                sx={{
                  width: 40,
                  height: 2,
                  borderRadius: 2,
                  background: "rgba(255,255,255,0.1)",
                }}
              />

              <VuiTypography variant="caption" sx={{ mt: 1.5, opacity: 0.6 }}>
                Data will appear here automatically
              </VuiTypography>
            </>
          )}
        </VuiBox>
      </Card>
    );
  }

  return (
    <Card>
      <VuiBox>
        <VuiBox mb={1.5}>
          <VuiTypography variant="lg" fontWeight="bold" sx={{ display: "block", lineHeight: 1.2 }}>
            {insights.title || "Guest Preference Insights"}
          </VuiTypography>

          <VuiTypography variant="caption" sx={{ display: "block", mt: 0.5 }}>
            {insights.subtitle || "Preferences captured through guest interaction flows"}
          </VuiTypography>
        </VuiBox>

        {/* ================= SUMMARY CARDS ================= */}
        {insights.summaryCards?.length > 0 && (
          <Grid container spacing={1.25} mb={1.5}>
            {insights.summaryCards.map((card, index) => (
              <Grid item xs={12} md={4} key={index}>
                {renderGlassCard({
                  px: 1.25,
                  py: 1,
                  children: (
                    <>
                      <VuiBox display="flex" alignItems="center" gap={0.75} mb={0.75}>
                        {getIcon(card.icon, { color: primary.main, size: 14 })}

                        <VuiTypography
                          variant="caption"
                          sx={{ fontSize: 11, fontWeight: 600, lineHeight: 1.3 }}
                        >
                          {card.title}
                        </VuiTypography>
                      </VuiBox>

                      <VuiTypography
                        variant="h5"
                        fontWeight="medium"
                        sx={{ fontSize: 18, lineHeight: 1.2 }}
                      >
                        {card.value}
                      </VuiTypography>

                      {card.sub && (
                        <VuiTypography
                          color={"success"}
                          variant="caption"
                          sx={{ display: "block", mt: 0.25, fontSize: 10, lineHeight: 1.3 }}
                        >
                          {card.sub}
                        </VuiTypography>
                      )}
                    </>
                  ),
                })}
              </Grid>
            ))}
          </Grid>
        )}

        {/* ================= OPERATIONAL NOTES ================= */}
        {insights.operationalNotes?.length > 0 && (
          <VuiBox mb={1.5}>
            {renderGlassCard({
              children: (
                <>
                  <VuiBox display="flex" alignItems="center" gap={1} mb={1}>
                    {getIcon("IoSparkles", { color: "#f59e0b", size: 15 })}

                    <VuiTypography variant="button" fontWeight="bold">
                      Operational Notes
                    </VuiTypography>
                  </VuiBox>

                  <Stack spacing={0.7}>
                    {insights.operationalNotes.map((note, i) => (
                      <VuiTypography key={i} variant="caption">
                        • {note}
                      </VuiTypography>
                    ))}
                  </Stack>
                </>
              ),
            })}
          </VuiBox>
        )}

        {/* ================= INSIGHT CARDS ================= */}
        <Grid container spacing={1.25}>
          {insights.insightCards?.map((card, index) => (
            <Grid item xs={12} md={6} xl={4} key={index}>
              {renderGlassCard({
                p: 1.25,
                children: (
                  <>
                    {/*  Header */}
                    <VuiBox display="flex" alignItems="center" gap={1.2} mb={1.25}>
                      <VuiBox
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                          height: 20,
                          width: 20,
                          lineHeight: 0,
                        }}
                      >
                        {getIcon(card.icon, { context: card, size: 14 })}
                      </VuiBox>

                      <VuiTypography
                        variant="button"
                        fontWeight="bold"
                        sx={{
                          lineHeight: 1,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {card.title}
                      </VuiTypography>
                    </VuiBox>

                    {/* Description */}
                    {card.description && (
                      <VuiTypography variant="caption" display="block" mb={1}>
                        {card.description}
                      </VuiTypography>
                    )}

                    {/* Items */}
                    <Stack spacing={0.75}>
                      {card.items?.map((item, idx) => (
                        <VuiBox
                          key={idx}
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          {/*  Label + sub */}
                          <VuiBox display="flex" flexDirection="column" sx={{ minWidth: 0 }}>
                            <VuiTypography
                              variant="caption"
                              sx={{
                                lineHeight: 1.2,
                                fontWeight: 500,
                                mb: item.sub ? 0.4 : 0, // add spacing if sub exists
                                display: "block",
                              }}
                            >
                              {item.label}
                            </VuiTypography>

                            {item.sub && (
                              <VuiTypography
                                variant="caption"
                                sx={{
                                  lineHeight: 1.1,
                                  opacity: 0.7,
                                  display: "block",
                                }}
                              >
                                {item.sub}
                              </VuiTypography>
                            )}
                          </VuiBox>

                          {/* Percentage value */}
                          <VuiTypography
                            variant="caption"
                            fontWeight="bold"
                            sx={{
                              minWidth: 40,
                              textAlign: "right",
                            }}
                          >
                            {item.value}
                          </VuiTypography>
                        </VuiBox>
                      ))}
                    </Stack>
                  </>
                ),
              })}
            </Grid>
          ))}
        </Grid>
      </VuiBox>
    </Card>
  );
}

export default GuestLifestyleInsights;
